import { Injectable, Injector, OnDestroy, ApplicationRef, ComponentRef, createComponent, EnvironmentInjector } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { interval, forkJoin, Observable, BehaviorSubject, Subscription, of } from 'rxjs';
import { OpenShiftsService } from 'src/app/modules/admin/services/open-shifts.service';
import { ShiftSwapService } from 'src/app/modules/swap-shift/services/shift-swap.service';
import { CalendarService } from 'src/app/modules/admin/services/calendar.service';
import { TimeRecordingService } from 'src/app/modules/time-recording/services/time-recording.service';
import dayjs from 'dayjs';
import { AbsenceService } from 'src/app/modules/absence/services/absence.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UpdateNotificationComponent } from '../../components/update-notification/update-notification.component';
import { isTerminalSession } from '../session/terminal-session';

/**
 * Öffentliche Einstiegsseiten, auf denen ein Update NIE automatisch angewendet wird.
 *
 * Ein `document.location.reload()` reißt hier dem Besucher die Seite unter den Händen weg —
 * und auf `/demo` kostet das messbar Conversions: Das Turnstile-Token lebt nur im Speicher der
 * Komponente, nach dem Reload ist es weg und der Startknopf wieder gesperrt. Belegt am
 * 19.08.2026 im Zugriffsprotokoll von `demo.fixplaner.com` (proxy-host-12): Beide Besucher des
 * Tages zeigten dasselbe Muster — Seite geladen, ~30 s später `ngsw.json` + erneut `index.html`,
 * danach kein Demo-Start. Auf einer Willkommens-/Formularseite ist die alte Version völlig
 * unkritisch; das Update greift beim nächsten echten Seitenaufruf.
 */
const NO_AUTO_RELOAD_ROUTES = ['/demo', '/registration', '/login', '/forgotpassword'];

@Injectable({
  providedIn: 'root',
})
export class UpdateService implements OnDestroy {
  private updateAvailable$ = new BehaviorSubject<boolean>(false);
  private toastr: ToastrService | null = null;
  private translate: TranslateService | null = null;
  private updateAlertShown = false;
  private subscriptions = new Subscription();
  private updateNotificationRef: ComponentRef<UpdateNotificationComponent> | null = null;

  constructor(
    private swUpdate: SwUpdate,
    private injector: Injector,
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector
  ) {
    // Lazy loading von Services um zirkuläre Abhängigkeiten zu vermeiden
    setTimeout(() => {
      this.toastr = this.injector.get(ToastrService);
      this.translate = this.injector.get(TranslateService);

      // Starte Update-Prüfung erst NACH Initialisierung der Services
      this.checkForUpdates();
    });

    // Nur Interval starten wenn Service Worker enabled ist
    // PERFORMANCE: Delay first check by 60s to avoid competing with page load resources.
    // The SW's own initialization already checks for updates on activation.
    if (swUpdate.isEnabled) {
      const timeoutId = setTimeout(() => {
        const intervalSub = interval(30 * 60 * 1000).subscribe(() =>
          swUpdate.checkForUpdate().then()
        );
        this.subscriptions.add(intervalSub);
      }, 60_000);
      // Store timeout for cleanup
      this.subscriptions.add(() => clearTimeout(timeoutId));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroyUpdateNotification();
  }

  /**
   * Observable das anzeigt, ob ein Update verfügbar ist
   */
  get updateAvailable(): Observable<boolean> {
    return this.updateAvailable$.asObservable();
  }

  public checkForUpdates(): void {
    const updateSub = this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.updateAvailable$.next(true);

        // Öffentliche Einstiegsseite: weder reloaden noch nachfragen (siehe NO_AUTO_RELOAD_ROUTES).
        if (this.isNoAutoReloadRoute()) {
          console.log(
            '[UpdateService] VERSION_READY auf öffentlicher Einstiegsseite — Update aufgeschoben.'
          );
          return;
        }

        // Auto-activate if no unsaved changes, otherwise show notification
        if (!this.hasUnsavedChanges()) {
          console.log('[UpdateService] VERSION_READY detected, auto-activating update...');
          this.swUpdate.activateUpdate().then(() => {
            document.location.reload();
          });
        } else {
          console.log('[UpdateService] VERSION_READY detected, but unsaved changes present. Showing notification.');
          this.showUpdateNotification();
        }
      }
    });
    this.subscriptions.add(updateSub);
  }

  /**
   * Zeigt eine benutzerfreundliche Update-Benachrichtigung
   * Benutzer kann selbst entscheiden, wann er aktualisieren möchte
   */
  private async showUpdateNotification(): Promise<void> {
    if (this.updateAlertShown) {
      return;
    }

    this.updateAlertShown = true;

    // Erstelle die neue Update-Notification-Komponente
    this.createUpdateNotification();
  }

  /**
   * Erstellt und zeigt die Update-Notification-Komponente dynamisch an
   */
  private createUpdateNotification(): void {
    // Verhindere mehrfache Instanzen
    if (this.updateNotificationRef) {
      return;
    }

    // Erstelle die Komponente dynamisch
    this.updateNotificationRef = createComponent(UpdateNotificationComponent, {
      environmentInjector: this.environmentInjector
    });

    // Setze die Callback-Funktionen
    this.updateNotificationRef.instance.onUpdateNow = () => {
      this.applyUpdate();
      this.destroyUpdateNotification();
    };

    this.updateNotificationRef.instance.onUpdateLater = () => {
      this.updateAlertShown = false; // Erlaube erneutes Anzeigen beim nächsten Check
      this.destroyUpdateNotification();
    };

    // Füge die Komponente zum DOM hinzu
    document.body.appendChild(this.updateNotificationRef.location.nativeElement);

    // Registriere die Komponente bei Angular
    this.appRef.attachView(this.updateNotificationRef.hostView);
  }

  /**
   * Entfernt die Update-Notification-Komponente
   */
  private destroyUpdateNotification(): void {
    if (this.updateNotificationRef) {
      this.appRef.detachView(this.updateNotificationRef.hostView);
      this.updateNotificationRef.destroy();
      this.updateNotificationRef = null;
    }
  }

  /**
   * Wendet das Update an - nur wenn der Benutzer bereit ist
   * Zeigt vorher eine Warnung, wenn ungespeicherte Änderungen vorhanden sein könnten
   */
  public applyUpdate(): void {
    // Prüfe ob ungespeicherte Änderungen vorhanden sein könnten
    if (this.hasUnsavedChanges()) {
      const confirmMessage = this.translate?.instant('UPDATE_UNSAVED_CHANGES_WARNING') ||
        'Sie haben möglicherweise ungespeicherte Änderungen. Möchten Sie wirklich aktualisieren?';

      if (!confirm(confirmMessage)) {
        return;
      }
    }

    this.swUpdate.activateUpdate().then(() => {
      document.location.reload();
    });
  }

  /**
   * True, wenn die aktuell angezeigte Route ein automatisches Update verbietet.
   *
   * Gelesen wird bewusst `window.location.pathname` statt des Routers: Der UpdateService wird sehr
   * früh erzeugt, und ein `Router`-Zugriff im Konstruktor-Umfeld hat hier schon zu zirkulären
   * Abhängigkeiten geführt (siehe Lazy-Loading der Services oben).
   */
  private isNoAutoReloadRoute(): boolean {
    const path = (window.location?.pathname ?? '').toLowerCase();
    return NO_AUTO_RELOAD_ROUTES.some(
      (route) => path === route || path.startsWith(route + '/')
    );
  }

  /**
   * Prüft ob möglicherweise ungespeicherte Änderungen vorhanden sind
   * Dies ist eine einfache Heuristik - kann erweitert werden
   */
  private hasUnsavedChanges(): boolean {
    // Prüfe auf dirty forms im DOM (nur ng-dirty, nicht ng-touched!)
    const dirtyForms = document.querySelectorAll('form.ng-dirty');
    if (dirtyForms.length > 0) {
      return true;
    }

    // Prüfe auf ungespeicherte Änderungen in localStorage/sessionStorage
    const hasUnsavedInStorage = sessionStorage.getItem('hasUnsavedChanges') === 'true';

    return hasUnsavedInStorage;
  }

  /**
   * Erzwingt ein Update (für ChunkLoadError)
   * Wird nur verwendet, wenn die Anwendung nicht mehr funktioniert
   */
  public forceReload(): void {
    const reloadKey = 'chunk-load-error-reload';
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();

    // Nur neu laden, wenn der letzte Reload mehr als 10 Sekunden her ist
    if (!lastReload || (now - parseInt(lastReload, 10)) > 10000) {
      sessionStorage.setItem(reloadKey, now.toString());

      // Zeige kurze Info-Nachricht
      if (this.toastr && this.translate) {
        const message = this.translate.instant('RELOADING_APP') || 'Anwendung wird aktualisiert...';
        this.toastr.info(message, '', {
          timeOut: 2000,
          positionClass: 'toast-top-center'
        });
      }

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  /**
   * Lädt die Daten der Mitarbeiter-App neu. Auf einem Terminal nichts: Es gehört keiner Person
   * (siehe `ForegroundRefreshService`). Die Prüfung steht auch hier, damit kein künftiger
   * Aufrufer sie umgeht.
   */
  public performRefresh(): Observable<any> {
    if (isTerminalSession()) {
      return of(null);
    }

    const openShiftService = this.injector.get(OpenShiftsService);
    const shiftSwapService = this.injector.get(ShiftSwapService);
    const calendarService = this.injector.get(CalendarService);
    const timeRecordingService = this.injector.get(TimeRecordingService);
    const absenceService = this.injector.get(AbsenceService);

    timeRecordingService.timeRange = {
      StarTime: dayjs().startOf('month').toDate(),
      EndTime: dayjs().toDate(),
    };

    return forkJoin({
      openShifts: openShiftService.getOpenShiftsForEmployee(),
      // Zwei getrennte Endpunkte, zwei getrennte Fachbegriffe: `GetHistory` liefert die
      // eigenen Vorgänge, `GetPending` die Angebote anderer. Bis zum 31.08.2026 stand hier
      // nur der erste Aufruf, weil er beide Ströme befüllte — die Kachel „Tauschangebote"
      // zeigte deshalb die eigenen Anfragen.
      employeeSwapShiftsList: shiftSwapService.GetEmployeeSwapShiftsListAsync(),
      swapOffersFromColleagues: shiftSwapService.GetShiftsForSwapQueryAsync(),
      notAcceptedEvents: calendarService.getNotAcceptedEventFromEmployee(),
      events: calendarService.getEvent(),
      clockEvents: timeRecordingService.getClockEventsForMultipleDays(
        timeRecordingService.timeRange.StarTime,
        timeRecordingService.timeRange.EndTime
      ),
      absenceEvents: absenceService.getAbsenceAsync(),
    });
  }
}
